import { BrowserFileUpload } from '@/platform/upload/BrowserFileUpload'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { getProjectResourceUrl } from '@/material/web/api'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { resolveVirtualResourcePathMeta } from '@/features/virtual-resource/rules'

/** 素材上传阶段。素材上传入口对外把 MD5、预检查、秒传、真实上传合并为连续的 uploading 进度。 */
export type MaterialUploadProgressStage = 'hashing' | 'uploading' | 'queue'

export type MaterialUploadResult = {
  fileName: string
  md5: string
  url: string
  size: number
}

export type MaterialUploadItem = {
  file: File
  objectName: string
  resultFileName?: string
}

const MATERIAL_HASH_CONCURRENCY = 4
const MATERIAL_UPLOAD_OPERATIONS = ['3', '0'] as const

function joinPath(...parts: Array<string | undefined>): string {
  return parts
    .map((part) => String(part || '').replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/')
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length)
  let nextIndex = 0
  const workerCount = Math.min(Math.max(1, limit), items.length)
  const workers = Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      results[currentIndex] = await mapper(items[currentIndex]!, currentIndex)
    }
  })
  await Promise.all(workers)
  return results
}

export function buildProjectResourceObjectName(params: {
  guid: string
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
}): string {
  const meta = resolveVirtualResourcePathMeta(
    params.category,
    params.folderSegments.join('/'),
    params.fileName,
  )
  return joinPath(params.guid, meta.mappingPath, meta.relativePath).toLowerCase()
}

/** 轮询上传队列，直到服务端完成异步入库。 */
async function waitForUploadQueue(upload: BrowserFileUpload): Promise<void> {
  let queueCount = 1
  while (queueCount > 0) {
    const response = await upload.getQueueInfo()
    const payload = JSON.parse(response) as { data?: { queue_count?: number } }
    queueCount = Number(payload.data?.queue_count ?? 0)
    if (queueCount > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 1200))
    }
  }
}

/**
 * 将下载到浏览器内存的素材文件重新上传到橙光资源存储，
 * 返回项目内可直接复用的资源 URL。
 */
export async function uploadMaterialFile(params: {
  file: File
  guid: string
  sessionId: string
  version: string
  objectName: string
  resultFileName?: string
  onProgress?: (stage: MaterialUploadProgressStage, percent: number) => void
}): Promise<MaterialUploadResult> {
  const resultFileName = params.resultFileName?.trim() || params.file.name
  let emittedPercent = 0
  const emitUploadProgress = (percent: number) => {
    emittedPercent = Math.max(emittedPercent, Math.max(0, Math.min(100, Math.round(percent))))
    params.onProgress?.('uploading', emittedPercent)
  }
  const md5 = await BrowserFileUtils.getMd5HashFromFile(params.file, (percent) => {
    emitUploadProgress(percent * 0.35)
  })

  const uploadTasks = MATERIAL_UPLOAD_OPERATIONS.map((op) => ({
    file: params.file,
    fileName: params.objectName,
    progressKey: `${params.objectName}:${op}`,
    op,
    shared: '1',
    contentMd5: md5,
  }))
  const uploadProgress = new Map<string, number>()
  const upload = new BrowserFileUpload(
    {
      guid: params.guid,
      version: params.version,
      proj_ver: params.version,
      sessionid: params.sessionId,
    },
    uploadTasks,
    (_fileName, percent, status) => {
      if (status === 'uploading' || status === 'completed') {
        uploadProgress.set(_fileName, Math.max(0, Math.min(100, Math.round(percent))))
        const totalPercent =
          uploadTasks.reduce((sum, item) => sum + (uploadProgress.get(item.progressKey) ?? 0), 0) / uploadTasks.length
        emitUploadProgress(35 + totalPercent * 0.6)
      }
    },
  )

  await upload.uploadFile()
  emitUploadProgress(96)
  await waitForUploadQueue(upload)
  emitUploadProgress(100)

  const url = getProjectResourceUrl(md5)

  return {
    fileName: resultFileName,
    md5,
    url,
    size: params.file.size,
  }
}

export async function uploadMaterialFiles(params: {
  guid: string
  sessionId: string
  version: string
  items: MaterialUploadItem[]
  onProgress?: (stage: MaterialUploadProgressStage, percent: number) => void
}): Promise<MaterialUploadResult[]> {
  if (!params.items.length) return []

  let emittedPercent = 0
  const emitUploadProgress = (percent: number) => {
    emittedPercent = Math.max(emittedPercent, Math.max(0, Math.min(100, Math.round(percent))))
    params.onProgress?.('uploading', emittedPercent)
  }
  const hashProgress = params.items.map(() => 0)
  const prepared = await mapWithConcurrency(params.items, MATERIAL_HASH_CONCURRENCY, async (item, index) => {
    const fileName = item.resultFileName?.trim() || item.file.name
    const md5 = await BrowserFileUtils.getMd5HashFromFile(item.file, (percent) => {
      if (!params.onProgress) return
      hashProgress[index] = Math.max(0, Math.min(100, Math.round(percent)))
      const totalPercent = hashProgress.reduce((sum, value) => sum + value, 0) / hashProgress.length
      emitUploadProgress(totalPercent * 0.35)
    })
    hashProgress[index] = 100
    if (params.onProgress) {
      const totalPercent = hashProgress.reduce((sum, value) => sum + value, 0) / hashProgress.length
      emitUploadProgress(totalPercent * 0.35)
    }
    return {
      ...item,
      fileName,
      md5,
    }
  })

  const uploadProgress = new Map<string, number>()
  const uploadTasks = prepared.flatMap((item, index) =>
    MATERIAL_UPLOAD_OPERATIONS.map((op) => ({
      file: item.file,
      fileName: item.objectName,
      progressKey: `${index}:${op}`,
      op,
      shared: '1',
      contentMd5: item.md5,
    })),
  )
  const upload = new BrowserFileUpload(
    {
      guid: params.guid,
      version: params.version,
      proj_ver: params.version,
      sessionid: params.sessionId,
    },
    uploadTasks,
    (_fileName, percent, status) => {
      if (status === 'uploading' || status === 'completed') {
        uploadProgress.set(_fileName, Math.max(0, Math.min(100, Math.round(percent))))
        const totalPercent =
          uploadTasks.reduce((sum, item) => sum + (uploadProgress.get(item.progressKey) ?? 0), 0) / uploadTasks.length
        emitUploadProgress(35 + totalPercent * 0.6)
      }
    },
  )
  await upload.uploadFile()
  emitUploadProgress(96)
  await waitForUploadQueue(upload)
  emitUploadProgress(100)

  const results = prepared.map((item) => ({
    fileName: item.fileName,
    md5: item.md5,
    url: getProjectResourceUrl(item.md5),
    size: item.file.size,
  }))
  return results
}
